/**
 * «Финансы → P&L»: месяцы × статьи, суммы со знаком (приход +, расход −).
 * Итоговые строки считает сервер — одно место правды о том, что такое прибыль.
 * Переводы между своими счетами, собственник, кредиты — ниже, вне P&L.
 *
 * Спека: `docs/specs/2026-09-29-bank-operations-pnl-design.md`.
 */
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import {
  ENTREPRENEUR_KEY,
  EntrepreneurPicker,
  entrepreneurIds as idsFrom,
  withEntrepreneurs,
} from "@/components/EntrepreneurPicker";
import { NoAccess } from "@/components/NoAccess";
import { PNL, useCan } from "@/lib/access";
import {
  CHANNEL_LABEL,
  type Channel,
  loadPnl,
  money,
  monthLabel,
  type Pnl,
  type PnlLine,
  type PnlRow,
  type PnlSource,
} from "@/lib/pnl";
import { type DirectoryRow, ENTREPRENEURS, loadDirectory } from "@/lib/statements";

const CELL = "whitespace-nowrap border-b border-border px-2.5 py-1 text-right tabular-nums";
const STICKY = "sticky left-0 z-10 min-w-[240px] whitespace-nowrap border-b border-r border-border px-3 py-1 text-left";
/** Шапка липнет к верху при прокрутке строк: над липким левым столбцом (z-10). */
const HEAD = "sticky top-0 z-20 bg-bg-subtle py-2 font-medium";
/** Строка группы — разделитель: линия сверху и фон, видно смену типа. */
const GROUP = "border-t-2 border-t-border-strong";

/** Итоговая строка встаёт после группы с этим кодом. */
const LINES_AFTER: Record<string, { key: Exclude<keyof Pnl["totals"], "unassigned_by_channel">; label: string }[]> = {
  interest: [{ key: "before_tax", label: "Прибыль до налогов" }],
  taxes: [{ key: "net", label: "Чистая прибыль" }],
  capex: [{ key: "after_capex", label: "Остаток после инвестиций" }],
};

export function PnlPage() {
  return useCan(PNL) ? <PnlScreen /> : <NoAccess what="P&L" />;
}

/** Ключи фильтров P&L в адресе; они же — последние фильтры в localStorage. */
const FILTER_KEYS = [ENTREPRENEUR_KEY, "business", "personal", "cash", "date_from", "date_to"];

/** Тип расхода — откуда деньги; каждый включается отдельно, в адресе — `=false`. */
const MONEY_TYPES: { key: "business" | "personal" | "cash"; label: string; title: string }[] = [
  { key: "business", label: "Безнал ИП", title: "Операции выписок по счетам ИП" },
  { key: "personal", label: "Безнал физик", title: "Операции выписок по личным счетам и картам" },
  { key: "cash", label: "Наличные", title: "Наличные кассы CRM: выплаты — расходы, оплаты наличными — выручка" },
];
const STORE = "finance.pnl.filters";

/** Порядок подстрок «Не разнесено: расходы»: сначала то, что прошло через банк. */
const CHANNELS: Channel[] = ["cashless", "person", "card", "withdraw", "cash"];

/** Только фильтры P&L из адреса: `article_id` и прочее чужое не сохраняем. */
function filtersOf(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams();
  for (const [key, value] of params) if (FILTER_KEYS.includes(key) && value) next.append(key, value);
  return next;
}

/** Последние фильтры. localStorage бывает недоступен (приватное окно) — тогда их нет. */
function storedFilters(): URLSearchParams | null {
  try {
    const raw = localStorage.getItem(STORE);
    return raw ? filtersOf(new URLSearchParams(raw)) : null;
  } catch {
    return null;
  }
}

function PnlScreen() {
  const navigate = useNavigate();
  // Фильтры живут в адресе — ссылку можно переслать, «назад» из «Зарплаты»
  // приходит с её ИП и периодом. Голый `/pnl` поднимает последние фильтры.
  const [params, setParams] = useSearchParams();
  const filters = filtersOf(params);
  const query = filters.toString();
  const [restore] = useState(() => (query ? null : storedFilters()));
  const restoring = restore !== null && restore.toString() !== "" && query === "";

  const entrepreneurIds = idsFrom(filters);
  const business = filters.get("business") !== "false";
  const personal = filters.get("personal") !== "false";
  const on = { business, personal, cash: filters.get("cash") !== "false" };
  const dateFrom = filters.get("date_from") ?? "";
  const dateTo = filters.get("date_to") ?? "";

  const [entrepreneurs, setEntrepreneurs] = useState<DirectoryRow[]>([]);
  const [report, setReport] = useState<Pnl | null>(null);
  const [failure, setFailure] = useState("");

  useEffect(() => {
    if (restoring) setParams(restore, { replace: true });
  }, [restoring, restore, setParams]);

  /** Правка фильтров: в адрес (без записи в историю) и в localStorage. */
  const change = (edit: (next: URLSearchParams) => URLSearchParams | void) => {
    const draft = filtersOf(params);
    const next = filtersOf(edit(draft) ?? draft);
    setParams(next, { replace: true });
    try {
      localStorage.setItem(STORE, next.toString());
    } catch {
      // без памяти о фильтрах экран всё равно работает
    }
  };
  const setFlag = (key: string, on: boolean) =>
    change((next) => {
      if (on) next.delete(key);
      else next.set(key, "false");
    });
  const setPeriod = (from: string, to: string) =>
    change((next) => {
      next.delete("date_from");
      next.delete("date_to");
      if (from) next.set("date_from", from);
      if (to) next.set("date_to", to);
    });

  useEffect(() => {
    loadDirectory(ENTREPRENEURS, true)
      .then(setEntrepreneurs)
      .catch((error: Error) => setFailure(error.message));
  }, []);

  useEffect(() => {
    if (restoring) return;
    const filters = new URLSearchParams(query);
    const entrepreneurIds = idsFrom(filters);
    const business = filters.get("business") !== "false";
    const personal = filters.get("personal") !== "false";
    const cash = filters.get("cash") !== "false";
    const dateFrom = filters.get("date_from") ?? "";
    const dateTo = filters.get("date_to") ?? "";
    setReport(null);
    setFailure("");
    loadPnl({
      entrepreneur_id: entrepreneurIds,
      business,
      personal,
      cash,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    })
      .then(setReport)
      .catch((error: Error) => setFailure(error.message));
  }, [query, restoring]);

  /** Фильтры P&L для перехода: ИП, период (и статья, если дана). Клик по
   *  ячейке месяца — только этот месяц, в пределах выбранного периода. */
  const scope = (articleId?: number, month?: string) => {
    const params = withEntrepreneurs(new URLSearchParams(articleId ? { article_id: String(articleId) } : {}), entrepreneurIds);
    const [from, to] = month ? monthBounds(month, dateFrom, dateTo) : [dateFrom, dateTo];
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    return params;
  };

  /** Тип денег — в адрес перехода: там та же выборка, что в ячейке P&L. */
  const withMoney = (params: URLSearchParams, cash = false) => {
    if (!business) params.set("business", "false");
    if (!personal) params.set("personal", "false");
    if (cash && !on.cash) params.set("cash", "false");
    return params;
  };

  /** Куда ведёт строка статьи (за месяц или за весь период). */
  const rowUrl = (row: PnlRow, month?: string): string => {
    // Зарплата — свой экран: наличные и банк, по салонам, сотрудникам, ролям.
    // Без наличных в строке одни операции банка — их список и отвечает «кому».
    if (row.code === "payroll" && on.cash) {
      return `/pnl/payroll?${withMoney(scope(undefined, month), true)}`;
    }
    // Банковское обслуживание — свой экран: виды платежей, банки, штуки.
    if (row.code === "bank_service") {
      const params = scope(undefined, month);
      if (!business) params.set("business", "false");
      if (!personal) params.set("personal", "false");
      if (!on.cash) params.set("cash", "false");
      return `/pnl/bank-service?${params}`;
    }
    const params = scope(row.id, month);
    // Строка только из наличных касс — её строки на экране «Кассы»:
    // выручка наличными — вкладка оплат, остальное — выплаты.
    if (row.operations === 0 && row.kassa_rows > 0) {
      params.delete("article_id");
      if (row.code === "rev_cash") params.set("flow", "payments");
      else params.set("article_id", String(row.id));
      params.set("kind", "cash");
      return `/kassa?${params}`;
    }
    return `/pnl/operations?${withMoney(params)}`;
  };

  const unassigned = report?.rows.find((r) => r.code === "unassigned");
  /** Расходы без статьи в ячейке — одни наличные касс: контрагенты строятся
   *  только по операциям банка, их список был бы пуст. */
  const cashOnly = (month?: string): boolean => {
    const at = (line: PnlLine | undefined) => Number((month ? line?.values[month] : line?.total) ?? 0);
    return at(report?.totals.unassigned_by_channel.cash) === at(report?.totals.unassigned_expense);
  };
  // «Не разнесено» по контрагентам: одно правило разносит пачку операций.
  const unassignedUrl = (direction?: "in" | "out", month?: string): string => {
    if (direction === "out" && cashOnly(month)) return channelUrl("cash", month);
    const params = scope(unassigned?.id, month);
    if (direction) params.set("direction", direction);
    if (!business) params.set("business", "false");
    if (!personal) params.set("personal", "false");
    return `/pnl/counterparties?${params}`;
  };
  /** Подстрока «Не разнесено: расходы»: банк — по контрагентам этого канала,
   *  наличные — выплаты касс без статьи. */
  const channelUrl = (channel: Channel, month?: string): string => {
    const params = scope(unassigned?.id, month);
    if (channel === "cash") {
      params.set("kind", "cash");
      return `/kassa?${params}`;
    }
    params.set("direction", "out");
    params.set("channel", channel);
    if (!business) params.set("business", "false");
    if (!personal) params.set("personal", "false");
    return `/pnl/counterparties?${params}`;
  };
  /** Клик по сумме — в новой вкладке: матрица P&L остаётся на месте. */
  const tab = (url: string) => window.open(url, "_blank", "noopener");

  /** Строка «Откуда ушли расходы»: расходы P&L её счетов; без счетов —
   *  наличные кассы, их выплаты на экране «Кассы». */
  const sourceUrl = (accountIds: number[], month?: string): string => {
    const params = scope(undefined, month);
    if (accountIds.length === 0) {
      params.set("kind", "cash");
      return `/kassa?${params}`;
    }
    for (const id of accountIds) params.append("account_id", String(id));
    params.set("expense", "true");
    return `/pnl/operations?${params}`;
  };

  // Выделенные строки: под каждой — доля от выручки.
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const pick = (key: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const share = (key: string, line: PnlLine, signed = false) =>
    report && picked.has(key) ? (
      <ShareRow line={line} income={report.totals.income} months={report.months} signed={signed} />
    ) : null;

  const transferBalance = Number(report?.totals.transfer_balance.total ?? 0);
  const unassignedExpense = Number(report?.totals.unassigned_expense.total ?? 0);
  const unassignedIncome = Number(report?.totals.unassigned_income.total ?? 0);

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <EntrepreneurPicker
          entrepreneurs={entrepreneurs}
          value={entrepreneurIds}
          onChange={(ids) => change((next) => withEntrepreneurs(next, ids))}
        />
        <div className="flex items-center gap-1">
          <span className="text-fg-faint">тип расхода</span>
          {MONEY_TYPES.map(({ key, label, title }) => {
            // Последний включённый не выключается: пустой P&L — не выбор.
            const last = on[key] && Object.values(on).filter(Boolean).length === 1;
            return (
              <button
                key={key}
                type="button"
                title={title}
                aria-pressed={on[key]}
                disabled={last}
                onClick={() => setFlag(key, !on[key])}
                className={`h-7 rounded-[var(--radius-ui)] border px-2.5 ${
                  on[key]
                    ? "border-accent/40 bg-accent-dim font-medium text-accent-text"
                    : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg"
                } disabled:cursor-default`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <DateRangePicker label="период" from={dateFrom} to={dateTo} onChange={setPeriod} />
      </div>

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}

      {report && unassigned && used(unassigned) > 0 ? (
        <div className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-3 py-2 text-[12.5px] text-warn">
          Не разнесено {used(unassigned)} строк. Расходы — {money(Math.abs(unassignedExpense))} ₽, уже вычтены из
          прибыли строкой «Не разнесено: расходы»; приходы — {money(unassignedIncome)} ₽, в прибыль не идут.{" "}
          <button type="button" className="underline" onClick={() => navigate(rowUrl(unassigned))}>
            разнести
          </button>
          {/* Контрагенты — только у операций банка; одни наличные касс — без них. */}
          {unassigned.operations > 0 ? (
            <>
              {" "}
              ·{" "}
              <button type="button" className="underline" onClick={() => navigate(unassignedUrl())}>
                по контрагентам
              </button>
            </>
          ) : null}
        </div>
      ) : null}
      {report && Math.abs(transferBalance) >= 1 ? (
        <div className="rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-3 py-2 text-[12.5px] text-fg-muted">
          Переводы между своими счетами не сходятся к нулю: {money(transferBalance)} ₽. Значит, загружены не все выписки —
          сторона перевода на другом счёте ещё не пришла.{" "}
          <Link to="/pnl/accounts" className="text-accent-text hover:underline">
            какие счета без выписок
          </Link>
        </div>
      ) : null}

      {report === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : report.months.length === 0 ? (
        <div className="text-[12.5px] text-fg-muted">
          Операций пока нет: загрузите выписки в разделе{" "}
          <Link to="/statements" className="text-accent-text hover:underline">
            «Выписки»
          </Link>
          .
        </div>
      ) : (
        // Таблица — сама прокручиваемая область (оба направления) на остаток
        // высоты: иначе липкой шапке не к чему липнуть. border-separate —
        // при collapse рамки липких ячеек остаются на месте при прокрутке.
        <div className="min-h-[240px] flex-1! overflow-auto rounded-[var(--radius-card)] border border-border">
          <table className="w-full border-separate border-spacing-0 text-[12.5px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                <th className="sticky left-0 top-0 z-30 min-w-[240px] whitespace-nowrap border-b border-r border-border bg-bg-subtle px-3 py-2 text-left font-medium">
                  статья
                </th>
                {report.months.map((m) => (
                  <th key={m} className={`${CELL} ${HEAD}`}>
                    {monthLabel(m)}
                  </th>
                ))}
                <th className={`${CELL} ${HEAD}`}>итого</th>
              </tr>
            </thead>
            <tbody>
              {report.rows
                .filter((r) => r.is_group)
                .map((group) => {
                  const children = report.rows.filter((r) => r.parent_id === group.id && shown(r));
                  const outside = !group.in_pnl && group.kind !== "capex";
                  const row = (r: PnlRow, strong = false) => (
                    <Fragment key={r.id}>
                      <Row
                        row={r}
                        months={report.months}
                        strong={strong}
                        muted={outside}
                        selected={picked.has(`row:${r.id}`)}
                        onSelect={() => pick(`row:${r.id}`)}
                        onOpen={(month) => tab(rowUrl(r, month))}
                      />
                      {share(`row:${r.id}`, r)}
                    </Fragment>
                  );
                  // Сальдо «Не разнесено» (приходы минус расходы) ничего не значит:
                  // расходы — выше, в прибыли; здесь — только приходы без статьи.
                  if (group.code === "unassigned") {
                    if (unassignedIncome === 0) return null;
                    const line = report.totals.unassigned_income;
                    return (
                      <Fragment key={group.id}>
                        <LineRow
                          label="Не разнесено: приходы"
                          title="Приходы без статьи — в прибыль не идут"
                          line={line}
                          months={report.months}
                          className="font-semibold text-warn"
                          selected={picked.has("unassigned_income")}
                          onSelect={() => pick("unassigned_income")}
                          onOpen={(month) => tab(unassignedUrl("in", month))}
                        />
                        {share("unassigned_income", line)}
                      </Fragment>
                    );
                  }
                  return (
                    <Fragment key={group.id}>
                      {group.code === "outside" ? <Spacer months={report.months.length} label="Вне P&L" /> : null}
                      {shown(group) || LINES_AFTER[group.code] ? row(group, true) : null}
                      {children.map((child) => row(child))}
                      {group.code === "fixed_costs" && unassignedExpense !== 0 ? (
                        <>
                          <LineRow
                            label="Не разнесено: расходы"
                            title="Расходы без статьи — вычтены из прибыли"
                            line={report.totals.unassigned_expense}
                            months={report.months}
                            className="text-warn"
                            selected={picked.has("unassigned_expense")}
                            onSelect={() => pick("unassigned_expense")}
                            onOpen={(month) => tab(unassignedUrl("out", month))}
                          />
                          {share("unassigned_expense", report.totals.unassigned_expense)}
                          {CHANNELS.filter((c) => Number(report.totals.unassigned_by_channel[c].total) !== 0).map((c) => (
                            <Fragment key={c}>
                              <LineRow
                                label={CHANNEL_LABEL[c]}
                                title={CHANNEL_HINT[c]}
                                line={report.totals.unassigned_by_channel[c]}
                                months={report.months}
                                className="text-fg-muted"
                                indent="pl-10"
                                selected={picked.has(`channel:${c}`)}
                                onSelect={() => pick(`channel:${c}`)}
                                onOpen={(month) => tab(channelUrl(c, month))}
                              />
                              {share(`channel:${c}`, report.totals.unassigned_by_channel[c])}
                            </Fragment>
                          ))}
                        </>
                      ) : null}
                      {(LINES_AFTER[group.code] ?? []).map((line) => (
                        <Fragment key={line.key}>
                          <Total
                            label={line.label}
                            line={report.totals[line.key]}
                            months={report.months}
                            selected={picked.has(`total:${line.key}`)}
                            onSelect={() => pick(`total:${line.key}`)}
                          />
                          {share(`total:${line.key}`, report.totals[line.key], true)}
                        </Fragment>
                      ))}
                    </Fragment>
                  );
                })}
              {report.sources.length > 0 ? (
                <Sources
                  report={report}
                  picked={picked}
                  onPick={pick}
                  share={share}
                  onOpen={(ids, month) => tab(sourceUrl(ids, month))}
                />
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}

/** Границы месяца `YYYY-MM`, урезанные выбранным периодом P&L: крайний
 *  месяц периода может быть неполным. */
function monthBounds(month: string, dateFrom: string, dateTo: string): [string, string] {
  const [year, mon] = month.split("-").map(Number);
  const last = new Date(year ?? 0, mon ?? 0, 0).getDate();
  const first = `${month}-01`;
  const end = `${month}-${String(last).padStart(2, "0")}`;
  return [dateFrom > first ? dateFrom : first, dateTo && dateTo < end ? dateTo : end];
}

/** Строк за статьёй: операций выписок и выплат наличных касс. */
function used(row: PnlRow): number {
  return row.operations + row.kassa_rows;
}

/** Строка видна, если в ней есть деньги: операции банка или поправки реестров
 *  эквайера — «Комиссия эквайринга» у CloudPayments бывает только из реестра. */
function shown(row: PnlRow): boolean {
  return used(row) > 0 || Number(row.acquirer_adjustment) !== 0;
}

function hint(row: PnlRow): string {
  const rows = row.kassa_rows ? `${row.operations} операций, ${row.kassa_rows} строк касс` : `${row.operations} операций`;
  const adjustment = Number(row.acquirer_adjustment)
    ? `; ещё ${money(row.acquirer_adjustment)} ₽ — из реестров эквайера и удержанной им комиссии (брутто и комиссия)`
    : "";
  return `${rows}${adjustment}. ${PICK_HINT}`;
}

/** Подсказка строки: клик по строке и клик по сумме делают разное. */
const PICK_HINT = "Клик по строке — выделить и показать долю от выручки; клик по сумме — открыть в новой вкладке";
/** Выделенная строка — фоном акцента, и её липкий столбец тоже. */
const PICKED = "bg-accent-dim";

function Row(props: {
  row: PnlRow;
  months: string[];
  strong?: boolean;
  muted?: boolean;
  selected: boolean;
  onSelect: () => void;
  onOpen: (month?: string) => void;
}) {
  const { row, months, strong, muted, selected } = props;
  const tone = muted ? "text-fg-muted" : "";
  const group = strong ? GROUP : "";
  const bg = selected ? PICKED : strong ? "bg-bg-subtle" : "bg-bg";
  return (
    <tr
      data-annot={`строка «${row.name}»`}
      className={`cursor-pointer ${selected ? PICKED : "hover:bg-bg-hover"} ${strong ? `${selected ? "" : "bg-bg-subtle"} font-semibold` : ""} ${tone}`}
      title={hint(row)}
      onClick={props.onSelect}
    >
      <td className={`${STICKY} ${bg} ${strong ? GROUP : "pl-6"}`}>{row.name}</td>
      {months.map((m) => (
        <ValueCell key={m} month={m} value={row.values[m]} className={group} onOpen={props.onOpen} />
      ))}
      <ValueCell value={row.total} className={`${group} font-medium`} onOpen={props.onOpen} />
    </tr>
  );
}

/** Ячейка суммы: клик — строки за этот месяц (итог — за весь период) в новой
 *  вкладке. Пустая ячейка не открывает ничего — клик уходит строке. */
function ValueCell(props: {
  month?: string;
  value: string | undefined;
  className?: string;
  /** Нет — сумма не открывается: списка за ней нет. */
  onOpen?: ((month?: string) => void) | undefined;
}) {
  const empty = !Number(props.value ?? 0) || !props.onOpen;
  const what = props.month ? `${monthLabel(props.month)} — открыть за этот месяц` : "Открыть за весь период";
  return (
    <td
      className={`${CELL} ${props.className ?? ""} ${empty ? "" : "hover:text-accent-text hover:underline"}`}
      title={empty ? undefined : `${what} в новой вкладке`}
      onClick={(e) => {
        if (empty) return;
        e.stopPropagation();
        props.onOpen?.(props.month);
      }}
    >
      {money(props.value, true)}
    </td>
  );
}

/** Строка итога сервера, у которой есть что открыть: неразнесённые расходы
 *  (всего и по каналу), приходы без статьи. */
function LineRow(props: {
  label: string;
  title: string;
  line: PnlLine;
  months: string[];
  className: string;
  indent?: string;
  selected: boolean;
  onSelect: () => void;
  onOpen?: ((month?: string) => void) | undefined;
}) {
  const { line, months, selected } = props;
  return (
    <tr
      data-annot={`строка «${props.label}»`}
      className={`cursor-pointer ${props.className} ${selected ? PICKED : "hover:bg-bg-hover"}`}
      title={`${props.title}. ${PICK_HINT}`}
      onClick={props.onSelect}
    >
      <td className={`${STICKY} ${selected ? PICKED : "bg-bg"} ${props.indent ?? "pl-6"}`}>{props.label}</td>
      {months.map((m) => (
        <ValueCell key={m} month={m} value={line.values[m]} onOpen={props.onOpen} />
      ))}
      <ValueCell value={line.total} className="font-medium" onOpen={props.onOpen} />
    </tr>
  );
}

const SOURCE_KIND: Record<PnlSource["kind"], string> = {
  business: "счёт ИП",
  personal: "личный",
  company: "счёт организации",
  cash: "Наличные кассы CRM",
  acquirer: "Удержано эквайером",
};

const SOURCE_HINT: Partial<Record<PnlSource["kind"], string>> = {
  cash: "Выплаты наличных касс CRM — по статьям и без статьи",
  acquirer: "Комиссия и возвраты, удержанные эквайером из зачислений: со счёта не уходили, в расходах P&L есть",
};

/** «Откуда ушли расходы»: человек (сумма его банков), под ним банк и тип
 *  счёта, затем кассы и эквайер; итог — расходы, налоги и капзатраты P&L. */
function Sources(props: {
  report: Pnl;
  picked: ReadonlySet<string>;
  onPick: (key: string) => void;
  share: (key: string, line: PnlLine) => ReactNode;
  onOpen: (accountIds: number[], month?: string) => void;
}) {
  const { report, picked, onPick, share, onOpen } = props;
  const banks = report.sources.filter((s) => s.entrepreneur_id !== null);
  const people = [...new Set(banks.map((s) => s.entrepreneur_id))].map((id) => {
    const own = banks.filter((s) => s.entrepreneur_id === id);
    return { id, name: own[0]?.entrepreneur_name ?? "", own, line: sumLines(own, report.months) };
  });
  const row = (key: string, label: string, title: string, line: PnlLine, strong: boolean, open?: (month?: string) => void) => (
    <Fragment key={key}>
      <LineRow
        label={label}
        title={title}
        line={line}
        months={report.months}
        className={strong ? "font-semibold" : "text-fg-muted"}
        indent={strong ? "pl-3" : "pl-6"}
        selected={picked.has(key)}
        onSelect={() => onPick(key)}
        onOpen={open}
      />
      {share(key, line)}
    </Fragment>
  );
  const hint = (operations: number, accounts: number) =>
    `${operations} операций, ${accounts === 1 ? "1 счёт" : `${accounts} счетов`}`;
  return (
    <>
      <Spacer months={report.months.length} label="Откуда ушли расходы" />
      {people.map((p) => {
        const ids = p.own.flatMap((s) => s.account_ids);
        const operations = p.own.reduce((n, s) => n + s.operations, 0);
        return (
          <Fragment key={`person:${p.id}`}>
            {row(`source:${p.id}`, p.name, hint(operations, ids.length), p.line, true, (month) => onOpen(ids, month))}
            {p.own.map((s) =>
              row(
                `source:${p.id}:${s.kind}:${s.bank_name ?? ""}`,
                `${s.bank_name ?? "банк не указан"} · ${SOURCE_KIND[s.kind]}`,
                hint(s.operations, s.account_ids.length),
                s,
                false,
                (month) => onOpen(s.account_ids, month),
              ),
            )}
          </Fragment>
        );
      })}
      {report.sources
        .filter((s) => s.entrepreneur_id === null)
        .map((s) =>
          row(
            `source:${s.kind}`,
            SOURCE_KIND[s.kind],
            SOURCE_HINT[s.kind] ?? "",
            s,
            true,
            // Удержанное эквайером — не операции и не выплаты: списка нет.
            s.kind === "cash" ? (month) => onOpen([], month) : undefined,
          ),
        )}
      <Total
        label="Итого расходов"
        line={report.totals.expense_total}
        months={report.months}
        selected={picked.has("total:expense_total")}
        onSelect={() => onPick("total:expense_total")}
      />
      {share("total:expense_total", report.totals.expense_total)}
    </>
  );
}

/** Сумма строк по месяцам — строка человека из строк его банков. */
function sumLines(lines: PnlLine[], months: string[]): PnlLine {
  const values: Record<string, string> = {};
  for (const m of months) {
    const v = lines.reduce((sum, l) => sum + Number(l.values[m] ?? 0), 0);
    if (v) values[m] = v.toFixed(2);
  }
  return { values, total: lines.reduce((sum, l) => sum + Number(l.total), 0).toFixed(2) };
}

const CHANNEL_HINT: Record<Channel, string> = {
  cashless: "Платёжки со счетов ИП организациям и ИП",
  person: "Со счёта ИП на счёт физлица и переводы с личных карт",
  card: "Покупки с личных карт: магазины, маркетплейсы, сервисы, такси",
  withdraw: "Снятие наличных с личных карт в банкомате",
  cash: "Выплаты наличных касс CRM без статьи",
};

function Total(props: { label: string; line: PnlLine; months: string[]; selected: boolean; onSelect: () => void }) {
  const { line, months, selected } = props;
  const tone = (v: string | undefined) => (Number(v ?? 0) < 0 ? "text-err" : "text-ok");
  const bg = selected ? PICKED : "bg-bg-subtle";
  return (
    <tr
      className={`cursor-pointer font-semibold ${bg}`}
      title="Клик — выделить и показать долю от выручки"
      onClick={props.onSelect}
    >
      <td className={`${STICKY} ${bg}`}>{props.label}</td>
      {months.map((m) => (
        <td key={m} className={`${CELL} ${tone(line.values[m])}`}>
          {money(line.values[m], true)}
        </td>
      ))}
      <td className={`${CELL} ${tone(line.total)}`}>{money(line.total, true)}</td>
    </tr>
  );
}

const PERCENT = new Intl.NumberFormat("ru-RU", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** Доля от выручки: у статьи — сколько она занимает (без знака), у итога
 *  прибыли — маржа со знаком. Выручки за месяц нет — доли нет. */
function percentOf(value: string | undefined, income: string | undefined, signed: boolean): string {
  const v = Number(value ?? 0);
  const base = Number(income ?? 0);
  if (!v || base <= 0) return "—";
  return PERCENT.format((signed ? v : Math.abs(v)) / base);
}

/** Под выделенной строкой: её доля от выручки по месяцам и за период. */
function ShareRow(props: { line: PnlLine; income: PnlLine; months: string[]; signed: boolean }) {
  const { line, income, months, signed } = props;
  return (
    <tr className={`${PICKED} text-[11px] text-accent-text`}>
      <td className={`${STICKY} ${PICKED} pl-6`}>доля от выручки</td>
      {months.map((m) => (
        <td key={m} className={CELL}>
          {percentOf(line.values[m], income.values[m], signed)}
        </td>
      ))}
      <td className={`${CELL} font-medium`}>{percentOf(line.total, income.total, signed)}</td>
    </tr>
  );
}

function Spacer(props: { months: number; label: string }) {
  return (
    <tr>
      <td
        colSpan={props.months + 2}
        className="border-b border-border px-3 pb-1 pt-4 text-[10.5px] font-medium uppercase tracking-[0.06em] text-fg-faint"
      >
        {props.label}
      </td>
    </tr>
  );
}
